import { useSearchParams } from "react-router-dom";
import { UrlBase64Obj } from "../UrlUtils/UrlBase64Obj.ts";
import { useCallback } from "react";
import _filter from "lodash.filter";
import { isDefined } from "../isDefined.ts";

const pageParamName = "p";
const filterParamName = "f";

function parsePage(value: string | null): number {
  const parsed = Number.parseInt(value ?? "", 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

export function useFilterAndPaginationController<T extends object>() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filterString = searchParams.get(filterParamName);
  const filter: Partial<T> = filterString
    ? UrlBase64Obj.fromString<T>(filterString).value
    : {};
  const currentPage = parsePage(searchParams.get(pageParamName));

  const toPage = useCallback(
    (page: number) => {
      const newSearchParams = new URLSearchParams(searchParams);
      if (page <= 0) newSearchParams.delete(pageParamName);
      else newSearchParams.set(pageParamName, String(page));
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams],
  );

  const setFilter = useCallback(
    (filter: T) => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete(pageParamName);

      if (
        _filter(filter, (f) => {
          if (!isDefined(f)) return false;
          return !(typeof f === "string" && f.length <= 0);
        }).length <= 0
      ) {
        newSearchParams.delete(filterParamName);
        setSearchParams(newSearchParams);
        return;
      }

      newSearchParams.set(filterParamName, new UrlBase64Obj(filter).toString());
      setSearchParams(newSearchParams);
    },
    [setSearchParams, searchParams],
  );

  return {
    currentPage,
    toPage,
    setFilter,
    filter,
  };
}
